import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  ChevronLeft, ChevronRight, CalendarCheck, PhoneCall, Printer, Trash2, RefreshCw, ExternalLink,
  AlarmClock, Check,
} from "lucide-react";
import { printEvent } from "@/lib/print";
import { isOverdue } from "@/lib/statuses";

const VIEWS = [
  { id: "day", label: "Jour" },
  { id: "week", label: "Semaine" },
  { id: "month", label: "Mois" },
];

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d) => addDays(startOfDay(d), -(((d.getDay() + 6) % 7)));
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

export default function CalendarPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [overdue, setOverdue] = useState({ count: 0, rappels: 0, rdv: 0, results: [] });
  const [selected, setSelected] = useState(null);

  const range = useMemo(() => {
    if (view === "day") return { from: startOfDay(cursor), to: addDays(startOfDay(cursor), 1) };
    if (view === "week") return { from: startOfWeek(cursor), to: addDays(startOfWeek(cursor), 7) };
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    return { from: startOfWeek(first), to: addDays(startOfWeek(first), 42) };
  }, [view, cursor]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events`, {
        params: { start: iso(range.from), end: iso(range.to) },
      });
      setEvents(r.data.results || []);
    } catch { toast.error("Impossible de charger le calendrier"); }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const loadOverdue = useCallback(() => {
    axios.get(`${API}/events/overdue`).then((r) => setOverdue(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadOverdue(); }, [loadOverdue]);

  const step = (dir) => {
    if (view === "day") setCursor((c) => addDays(c, dir));
    else if (view === "week") setCursor((c) => addDays(c, dir * 7));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
  };

  const title = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (view === "week") {
      const from = startOfWeek(cursor); const to = addDays(from, 6);
      return `${from.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} — ${to.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [view, cursor]);

  const eventsOf = (day) => events
    .filter((e) => sameDay(new Date(e.start), day))
    .sort((a, b) => a.start.localeCompare(b.start));

  const removeEvent = async (ev) => {
    try {
      await axios.delete(`${API}/events/${ev.id}`);
      toast.success("Événement supprimé");
      setSelected(null); load(); loadOverdue();
    } catch { toast.error("Suppression impossible"); }
  };

  const retrySync = async (ev) => {
    try {
      const r = await axios.post(`${API}/events/${ev.id}/sync`);
      toast[r.data.ok ? "success" : "info"](r.data.ok ? "Synchronisé avec Google Calendar" : "Google Calendar indisponible — RDV conservé dans Studio");
      setSelected({ ...ev, google_sync_status: r.data.google_sync_status });
      load();
    } catch { toast.error("Synchronisation impossible"); }
  };

  const completeEvent = async (ev) => {
    try {
      const r = await axios.post(`${API}/events/${ev.id}/complete`);
      toast.success(ev.type === "rappel" ? "Rappel marqué comme traité" : "RDV marqué comme traité");
      setSelected({ ...ev, completed_at: r.data.completed_at });
      loadOverdue(); load();
    } catch { toast.error("Action impossible"); }
  };

  const Chip = ({ ev }) => {
    const late = isOverdue(ev.start) && !ev.completed_at;
    const done = !!ev.completed_at;
    return (
      <button
        onClick={() => setSelected(ev)}
        data-testid={`calendar-event-${ev.id}`}
        className={`block w-full text-left rounded-md px-2 py-1.5 mb-1.5 border transition-colors ${
          done
            ? "bg-brand-paper text-brand-subtle border-brand-line line-through"
            : late
              ? "bg-[#FBF2EE] text-[#8A3F27] border-[#EBD5CB] hover:bg-[#F6E7E1]"
              : ev.type === "rdv"
                ? "bg-brand-moss text-white border-brand-moss hover:bg-brand-moss2"
                : "bg-white text-[#6B5B33] border-[#E9E2CF] border-dashed hover:bg-[#FAF8F1]"
        }`}
      >
        <div className="flex items-center gap-1.5 text-[10px] font-mono">
          {late
            ? <AlarmClock className="h-3 w-3" strokeWidth={1.75} />
            : ev.type === "rdv"
              ? <CalendarCheck className="h-3 w-3" strokeWidth={1.75} />
              : <PhoneCall className="h-3 w-3" strokeWidth={1.75} />}
          {new Date(ev.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          {late && " · retard"}
        </div>
        <div className="text-[11px] font-medium truncate mt-0.5">{ev.prospect_nom || ev.title}</div>
      </button>
    );
  };

  const days = useMemo(() => {
    const count = view === "day" ? 1 : view === "week" ? 7 : 42;
    return Array.from({ length: count }, (_, i) => addDays(range.from, i));
  }, [view, range.from]);

  return (
    <div className="min-h-screen">
      <AppHeader
        actions={
          <Button size="sm" onClick={() => setCursor(new Date())} data-testid="btn-today"
            className="bg-brand-moss hover:bg-brand-moss2 text-white rounded-md">
            Aujourd'hui
          </Button>
        }
      />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow">Calendrier Studio</div>
            <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mt-2 leading-none capitalize" data-testid="calendar-title">{title}</h1>
            <p className="text-xs text-brand-subtle mt-3">
              Uniquement les RDV et rappels créés depuis Studio. Vos événements Google personnels ne sont jamais importés.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-brand-line rounded-md overflow-hidden">
              <button onClick={() => step(-1)} data-testid="calendar-prev" className="h-9 w-9 flex items-center justify-center hover:bg-brand-paper transition-colors">
                <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
              </button>
              <div className="w-px h-9 bg-brand-line" />
              <button onClick={() => step(1)} data-testid="calendar-next" className="h-9 w-9 flex items-center justify-center hover:bg-brand-paper transition-colors">
                <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex items-center gap-1 border border-brand-line rounded-md p-1">
              {VIEWS.map((v) => (
                <button key={v.id} onClick={() => setView(v.id)} data-testid={`calendar-view-${v.id}`}
                  className={`px-3 h-7 rounded text-[10px] font-mono uppercase tracking-[0.14em] transition-colors ${
                    view === v.id ? "bg-brand-moss text-white" : "text-brand-subtle hover:text-brand-ink"
                  }`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5 mb-5 text-[10px] font-mono uppercase tracking-[0.14em] text-brand-subtle">
          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-brand-moss" /> RDV commercial</span>
          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-[#B79A4B]" /> Rappel téléphonique</span>
          <span className="flex items-center gap-2 text-[#8A3F27]"><span className="h-2.5 w-2.5 rounded-sm bg-[#FBF2EE] border border-[#EBD5CB]" /> En retard</span>
          <span className="ml-auto" data-testid="calendar-count">{events.length} événement{events.length > 1 ? "s" : ""}</span>
        </div>

        {overdue.count > 0 && (
          <div className="mb-5 rounded-md border border-[#EBD5CB] bg-[#FBF2EE] px-4 py-3" data-testid="overdue-banner">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-2 text-xs font-semibold text-[#8A3F27]">
                <AlarmClock className="h-4 w-4" strokeWidth={1.75} />
                {overdue.rappels} rappel{overdue.rappels > 1 ? "s" : ""} en retard
                {overdue.rdv > 0 && ` · ${overdue.rdv} RDV passé${overdue.rdv > 1 ? "s" : ""} non traité${overdue.rdv > 1 ? "s" : ""}`}
              </span>
              <div className="flex flex-wrap gap-2 ml-auto">
                {overdue.results.slice(0, 5).map((ev) => (
                  <button key={ev.id} onClick={() => setSelected(ev)} data-testid={`overdue-item-${ev.id}`}
                    className="text-[10px] font-mono px-2 py-1 rounded border border-[#EBD5CB] bg-white text-[#8A3F27] hover:bg-[#F6E7E1] transition-colors">
                    {new Date(ev.start).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} · {ev.prospect_nom}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <Card className="bg-white border-brand-line rounded-md shadow-card overflow-hidden" data-testid="calendar-grid">
          {view === "month" && (
            <div className="grid grid-cols-7 border-b border-brand-line bg-brand-paper">
              {DAY_LABELS.map((d) => (
                <div key={d} className="px-3 py-2.5 eyebrow text-center">{d}</div>
              ))}
            </div>
          )}
          <div className={view === "month" ? "grid grid-cols-7" : view === "week" ? "grid grid-cols-1 sm:grid-cols-7" : "grid grid-cols-1"}>
            {days.map((day) => {
              const list = eventsOf(day);
              const isToday = sameDay(day, new Date());
              const otherMonth = view === "month" && day.getMonth() !== cursor.getMonth();
              return (
                <div key={day.toISOString()}
                  className={`border-b border-r border-brand-line p-2.5 ${view === "month" ? "min-h-[118px]" : "min-h-[160px]"} ${otherMonth ? "bg-brand-paper/60" : "bg-white"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-mono text-[11px] ${isToday ? "text-white bg-brand-moss px-1.5 py-0.5 rounded" : otherMonth ? "text-brand-subtle/50" : "text-brand-subtle"}`}>
                      {view === "month"
                        ? day.getDate()
                        : `${DAY_LABELS[(day.getDay() + 6) % 7]} ${day.getDate()}`}
                    </span>
                    {list.length > 0 && <span className="font-mono text-[10px] text-brand-moss">{list.length}</span>}
                  </div>
                  {list.map((ev) => (<Chip key={ev.id} ev={ev} />))}
                </div>
              );
            })}
          </div>
        </Card>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="sm:max-w-md overflow-y-auto w-full" data-testid="event-sheet">
          {selected && (
            <div>
              <SheetHeader className="text-left">
                <div className="eyebrow">{selected.type === "rdv" ? "Rendez-vous commercial" : "Rappel téléphonique"}</div>
                <SheetTitle className="font-display text-2xl truncate">{selected.prospect_nom || selected.title}</SheetTitle>
                <SheetDescription className="font-mono text-[11px]">
                  {new Date(selected.start).toLocaleString("fr-FR")}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-7 space-y-5 text-sm">
                {[
                  ["Interlocuteur", [selected.interlocuteur, selected.interlocuteur_role].filter(Boolean).join(" · ")],
                  ["Téléphone", selected.prospect_phone],
                  ["Lieu / lien", selected.location],
                  ["Budget estimé", selected.budget],
                  ["Besoin détecté", selected.besoin],
                  ["Urgence", selected.urgence],
                  ["Objections", selected.objections],
                  ["Motif", selected.motif],
                  ["Note", selected.note],
                  ["Assigné à", selected.assigned_to],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}>
                    <div className="eyebrow mb-1">{k}</div>
                    <div className="text-brand-ink">{v}</div>
                  </div>
                ))}

                <Separator />
                {isOverdue(selected.start) && !selected.completed_at && (
                  <div className="rounded-md border border-[#EBD5CB] bg-[#FBF2EE] px-3 py-2 text-xs text-[#8A3F27] flex items-center gap-2" data-testid="event-overdue-flag">
                    <AlarmClock className="h-4 w-4" strokeWidth={1.75} /> Échéance dépassée
                  </div>
                )}
                {selected.completed_at && (
                  <div className="eyebrow" data-testid="event-completed-flag">
                    Traité le {new Date(selected.completed_at).toLocaleString("fr-FR")}
                  </div>
                )}
                <div className="eyebrow" data-testid="event-sync-status">
                  {selected.type === "rappel"
                    ? "Rappel Studio uniquement"
                    : selected.google_sync_status === "synchronise"
                      ? "Synchronisé avec Google Calendar"
                      : selected.google_sync_status === "non_configure"
                        ? "Google Calendar non configuré"
                        : "Synchronisation Google en attente"}
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  {!selected.completed_at && (
                    <Button size="sm" onClick={() => completeEvent(selected)} data-testid="event-complete"
                      className="bg-brand-ink hover:bg-brand-moss text-white rounded-md">
                      <Check className="h-3.5 w-3.5 mr-2" strokeWidth={2} /> Marquer traité
                    </Button>
                  )}
                  <Button size="sm" onClick={() => navigate(`/prospection?siret=${selected.prospect_siret}`)} data-testid="event-open-prospect"
                    className="bg-brand-moss hover:bg-brand-moss2 text-white rounded-md">
                    Ouvrir le prospect <ExternalLink className="h-3.5 w-3.5 ml-2" strokeWidth={1.75} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => printEvent(selected)} data-testid="event-print"
                    className="border-brand-line rounded-md hover:bg-brand-paper">
                    <Printer className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Imprimer
                  </Button>
                  {selected.type === "rdv" && selected.google_sync_status !== "synchronise" && (
                    <Button size="sm" variant="outline" onClick={() => retrySync(selected)} data-testid="event-retry-sync"
                      className="border-brand-line rounded-md hover:bg-brand-paper">
                      <RefreshCw className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Synchroniser
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => removeEvent(selected)} data-testid="event-delete"
                    className="border-[#E9DED8] text-[#7A4B3A] rounded-md hover:bg-[#F8F3F0]">
                    <Trash2 className="h-3.5 w-3.5 mr-2" strokeWidth={1.75} /> Supprimer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
